/**
 * Feature flags for staged rollouts.
 *
 * Resolution order:
 *   1. localStorage override (dev builds only — set by DevTools panel for engineers + Mike)
 *   2. VITE_ENABLE_HIRING env var (Cloudflare Pages env, set per environment)
 *   3. Default: enabled in `npm run dev`, disabled in production builds
 *
 * To flip Phase 2 live: set VITE_ENABLE_HIRING=true in the Cloudflare Pages
 * env vars panel + redeploy. No code change required.
 *
 * To preview hiring in production as an engineer: open DevTools panel, click
 * the "Hiring features" toggle, refresh.
 */

const HIRING_OVERRIDE_KEY = 'bmc_dev_hiring_override'

export function isHiringEnabled(): boolean {
  // 1. localStorage override — dev builds only (prevents console bypass in production)
  if (import.meta.env.DEV && typeof window !== 'undefined') {
    try {
      const ls = window.localStorage.getItem(HIRING_OVERRIDE_KEY)
      if (ls === 'true') return true
      if (ls === 'false') return false
    } catch {
      // localStorage may throw in some browser modes; fall through
    }
  }

  // 2. Build-time env var
  const env = import.meta.env.VITE_ENABLE_HIRING
  if (env === 'true') return true
  if (env === 'false') return false

  // 3. Default: dev = on, prod = off (Phase 1 launch state)
  return !!import.meta.env.DEV
}

/**
 * Messages (brand ↔ creator DMs) feature flag.
 *
 * Unlike hiring, messages default to OFF everywhere (dev + prod) — the feature
 * is hidden until explicitly switched on. To show it, set
 * VITE_ENABLE_MESSAGES=true (Cloudflare Pages env for prod, .env.local for dev)
 * and redeploy. Engineers can also flip it locally via the localStorage
 * override below (dev builds only) without touching env.
 */
const MESSAGES_OVERRIDE_KEY = 'bmc_dev_messages_override'

export function isMessagesEnabled(): boolean {
  // 1. localStorage override — dev builds only (prevents console bypass in production)
  // if (import.meta.env.DEV && typeof window !== 'undefined') {
  //   try {
  //     const ls = window.localStorage.getItem(MESSAGES_OVERRIDE_KEY)
  //     if (ls === 'true') return true
  //     if (ls === 'false') return false
  //   } catch {
  //     // localStorage may throw in some browser modes; fall through
  //   }
  // }

  // 2. Build-time env var
  const env = import.meta.env.VITE_ENABLE_MESSAGES
  if (env === 'true') return true
  if (env === 'false') return false

  // 3. Default: OFF everywhere until explicitly enabled.
  return false
}

export type MessagesOverride = 'true' | 'false' | null

export function getMessagesOverride(): MessagesOverride {
  if (typeof window === 'undefined') return null
  try {
    const v = window.localStorage.getItem(MESSAGES_OVERRIDE_KEY)
    if (v === 'true' || v === 'false') return v
  } catch { /* ignore */ }
  return null
}

export function setMessagesOverride(value: MessagesOverride): void {
  if (typeof window === 'undefined') return
  try {
    if (value === null) window.localStorage.removeItem(MESSAGES_OVERRIDE_KEY)
    else window.localStorage.setItem(MESSAGES_OVERRIDE_KEY, value)
  } catch { /* ignore */ }
}

export type HiringOverride = 'true' | 'false' | null

export function getHiringOverride(): HiringOverride {
  if (typeof window === 'undefined') return null
  try {
    const v = window.localStorage.getItem(HIRING_OVERRIDE_KEY)
    if (v === 'true' || v === 'false') return v
  } catch { /* ignore */ }
  return null
}

export function setHiringOverride(value: HiringOverride): void {
  if (typeof window === 'undefined') return
  try {
    if (value === null) window.localStorage.removeItem(HIRING_OVERRIDE_KEY)
    else window.localStorage.setItem(HIRING_OVERRIDE_KEY, value)
  } catch { /* ignore */ }
}
